import { reaction, when } from 'mobx';
import type { ProStudioNodeDataStore } from '@pypestream/pro-studio-collab-client';

import type { TableStore } from '../stores/table-store';
import { toVersionData, type CollabVersionData } from './collab-to-table-rows';
import { reportStoreFailure } from '../stores/utils/report-store-failure';
import { createCollabWriter } from './collab-writer';
import { toPresenceSelection } from './collab-presence';

export type CollabFeedTarget = Pick<
  TableStore,
  | 'processVersionData'
  | 'mergeVersionData'
  | 'setLiveViewSettings'
  | 'setLiveWriter'
  | 'convertCellText'
  | 'onLiveDocumentChange'
  | 'onLiveDocumentReset'
  | 'selectedStartCell'
  | 'selectedCells'
  | 'sortedRows'
  | 'isReadOnly'
  | 'onLiveEditabilityChange'
>;

/**
 * Keeps the Workflows table in step with the draft's collab document
 * (PSTUDIO-4450).
 *
 * Nothing reaches the table before the document's first sync: an unsynced
 * store is empty, and handing that over would show a draft with no rows. The
 * first snapshot is a full load (`processVersionData`, which also takes the
 * table off its loader); every later change — a remote edit, or this user's
 * own view settings — is a `mergeVersionData`, which writes only what differs.
 *
 * Both run with `skipValidation`: validation uploads the rows to bot-manager.
 *
 * One apply at a time, so a merge never overtakes the load still in flight
 * (`processVersionData` awaits a macrotask before it returns); changes that
 * land meanwhile are coalesced into the newest snapshot.
 *
 * From the first sync until it stops, it also points the table's
 * view-settings writes (collapse, column widths, pinning, wrap) at the store.
 */
export function startCollabTableFeed(
  store: ProStudioNodeDataStore,
  solutionVersionId: string,
  table: CollabFeedTarget
): () => void {
  let stopped = false;
  // Set once the table has ACCEPTED a load: `processVersionData` refuses a
  // payload for a version it is not on, and merging into a table that never
  // loaded would leave it on its loader for good.
  let loaded = false;
  let synced = false;
  // Coalesced: while an apply is in flight, later changes only replace
  // `latest`, and the drain applies the newest one next. A coworker typing
  // emits a change per keystroke and each apply rebuilds the projection, so
  // applying every snapshot in turn would leave the table behind the document.
  let latest: CollabVersionData | null = null;
  let draining = false;
  let sinkSet = false;
  const reportedDrafts = new Set<string>();
  const options = {
    forSolutionVersionId: solutionVersionId,
    skipValidation: true,
  };

  const drain = async () => {
    draining = true;
    try {
      while (latest && !stopped) {
        const data = latest;
        latest = null;
        try {
          if (loaded) {
            // eslint-disable-next-line no-await-in-loop
            await table.mergeVersionData(data, options);
          } else {
            // eslint-disable-next-line no-await-in-loop
            loaded = (await table.processVersionData(data, options)) === true;
            if (loaded) table.onLiveDocumentChange('load');
          }
        } catch (error: unknown) {
          console.error('Live editing: could not apply the document', error);
        }
      }
    } finally {
      draining = false;
    }
  };

  const dispose = reaction(
    () => {
      // Latched: going offline later keeps the rows the table already shows.
      synced ||= store.connectionStatus === 'synced';
      return synced
        ? toVersionData(
            store.orderedNodes,
            store.viewSettings,
            solutionVersionId
          )
        : null;
    },
    (data) => {
      if (!data) return;
      // Only a synced store has this user's view settings; a write into an
      // unsynced one would race the server's copy of the same key.
      if (!sinkSet) {
        sinkSet = true;
        table.setLiveViewSettings(store);
        // PSTUDIO-4451: and the table's saves, ordered by what it shows.
        table.setLiveWriter(
          // The raw order, as the legacy drag math uses it: the arranged one
          // pulls a row back into the group it still names mid-drag.
          createCollabWriter(store, () => table.sortedRows, {
            convert: table.convertCellText,
            onUnwritable: (draft) => {
              // Once per row and columns: the draft stays in the panel, and
              // every save of the row until it commits would report it again.
              const key = `${draft.rowId}:${draft.columns.join(',')}`;
              if (reportedDrafts.has(key)) return;
              reportedDrafts.add(key);
              reportStoreFailure(
                new Error('Array-column draft left out of a live write'),
                'live-write-draft-skipped',
                draft
              );
            },
          })
        );
      }
      latest = data;
      if (!draining) void drain();
    },
    { fireImmediately: true }
  );

  // PSTUDIO-4451: what changed the document decides when to validate — the
  // applies above pass `skipValidation`. After the load only; until then the
  // load itself validates.
  const disposeTransactions = store.onTransaction(({ origin }) => {
    // A coworker's change during the load is in the next merge, which skips
    // validation — so it still counts (debounced, it lands after the load).
    if (loaded || origin === 'remote') table.onLiveDocumentChange(origin);
  });

  // PSTUDIO-4452: an import replaced the document (`resetToken` moves only on
  // a reset seen after the load, never on the first sync).
  const disposeReset = reaction(
    () => store.resetToken,
    () => table.onLiveDocumentReset()
  );

  // PSTUDIO-4451: access can change mid-session (a role downgrade, an
  // import lock); the table follows it without a reload.
  const disposeEditability = reaction(
    () => table.isReadOnly,
    (readOnly) => table.onLiveEditabilityChange(readOnly)
  );

  return () => {
    stopped = true;
    dispose();
    disposeEditability();
    disposeReset();
    disposeTransactions();
    table.setLiveViewSettings(null);
    table.setLiveWriter(null);
  };
}

/**
 * PSTUDIO-4451: this user's selected cell is what coworkers see, and what the
 * store's undo hands back. Only once synced, like the writes.
 *
 * PSTUDIO-4449: its own lifetime, apart from the table feed. The feed runs on
 * every page of the solution and must not restart when the user moves between
 * them (a restart is a full reload and a validation round); the cursor is only
 * shared while the Workflows page is open.
 */
export function startPresenceShare(
  store: ProStudioNodeDataStore,
  table: Pick<CollabFeedTarget, 'selectedStartCell' | 'selectedCells'>
): () => void {
  let disposeShare: (() => void) | undefined;
  const disposeWait = when(
    () => store.connectionStatus === 'synced',
    () => {
      disposeShare = reaction(
        () => {
          const cell =
            table.selectedStartCell ??
            (table.selectedCells.length === 1 ? table.selectedCells[0] : null);
          return cell ? `${cell.rowId}\u0000${String(cell.columnId)}` : '';
        },
        (key) => {
          const [rowId, columnId] = key ? key.split('\u0000') : [];
          store.setLocalPresence({
            selection: toPresenceSelection(rowId ? { rowId, columnId } : null),
          });
        },
        { fireImmediately: true }
      );
    }
  );

  return () => {
    disposeWait();
    if (!disposeShare) return;
    disposeShare();
    // Coworkers stop seeing this user's cursor on a page they have left.
    store.setLocalPresence({ selection: undefined });
  };
}
